import { useMemo, useState } from "react";

export const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

/**
 * Client-side pagination over an in-memory list. The page index is clamped
 * rather than reset from an effect, so a row set that shrinks under the current
 * page — filtering, or revoking the last subscription on it — falls back to the
 * last non-empty page instead of rendering blank.
 */
export const usePagination = <T>(
  rows: T[],
  initialRowsPerPage = DEFAULT_ROWS_PER_PAGE_OPTIONS[0],
) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  const pageCount = Math.max(Math.ceil(rows.length / rowsPerPage), 1);
  const safePage = Math.min(page, pageCount - 1);

  const visibleRows = useMemo(
    () =>
      rows.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage),
    [rows, safePage, rowsPerPage],
  );

  const changeRowsPerPage = (value: number) => {
    setRowsPerPage(value);
    setPage(0);
  };

  return {
    page: safePage,
    rowsPerPage,
    visibleRows,
    setPage,
    setRowsPerPage: changeRowsPerPage,
  };
};
